import type { ModelSelection } from "@cmmd-forge/contracts";
import { memo, useMemo } from "react";

import type { ContextWindowSnapshot } from "~/lib/contextWindow";
import { ComposerPrimaryActions } from "./ComposerPrimaryActions";
import { PlanUsageContextWindowMeter, type PlanUsageThread } from "./PlanUsageContextWindowMeter";

/**
 * The composer footer's right-hand cluster: the context meter (with plan usage
 * when the thread has a Connected Subscription), the worktree-preparation
 * note, and the primary send/interrupt actions. Extracted from ChatComposer so
 * the meter's plan-usage inputs did not grow a file already past the size cap.
 */
export const ComposerFooterPrimaryActions = memo(function ComposerFooterPrimaryActions(props: {
  compact: boolean;
  activeContextWindow: ContextWindowSnapshot | null;
  activeThreadModelSelection: ModelSelection | null | undefined;
  activeThreadRateLimitStatus: PlanUsageThread["rateLimitStatus"];
  isPreparingWorktree: boolean;
  pendingAction: {
    questionIndex: number;
    isLastQuestion: boolean;
    canAdvance: boolean;
    isResponding: boolean;
    isComplete: boolean;
  } | null;
  isRunning: boolean;
  showPlanFollowUpPrompt: boolean;
  promptHasText: boolean;
  isSendBusy: boolean;
  isConnecting: boolean;
  hasSendableContent: boolean;
  isComposerDisabled: boolean;
  onPreviousPendingQuestion: () => void;
  onInterrupt: () => void;
  onImplementPlanInNewThread: () => void;
}) {
  // Only the two fields plan usage reads, so this memoized footer stays off
  // the streaming path: the thread object changes on every token, these do not.
  const { activeThreadModelSelection, activeThreadRateLimitStatus } = props;
  const planUsageThread = useMemo<PlanUsageThread | undefined>(
    () =>
      activeThreadModelSelection
        ? {
            modelSelection: activeThreadModelSelection,
            rateLimitStatus: activeThreadRateLimitStatus,
          }
        : undefined,
    [activeThreadModelSelection, activeThreadRateLimitStatus],
  );
  return (
    <>
      {props.activeContextWindow ? (
        <PlanUsageContextWindowMeter
          usage={props.activeContextWindow}
          activeThread={planUsageThread}
        />
      ) : null}
      {props.isPreparingWorktree ? (
        <span className="text-muted-foreground/70 text-xs">Preparing worktree...</span>
      ) : null}
      <ComposerPrimaryActions
        compact={props.compact}
        pendingAction={props.pendingAction}
        isRunning={props.isRunning}
        showPlanFollowUpPrompt={props.showPlanFollowUpPrompt}
        promptHasText={props.promptHasText}
        isSendBusy={props.isSendBusy}
        isConnecting={props.isConnecting}
        isPreparingWorktree={props.isPreparingWorktree}
        hasSendableContent={props.hasSendableContent}
        isComposerDisabled={props.isComposerDisabled}
        onPreviousPendingQuestion={props.onPreviousPendingQuestion}
        onInterrupt={props.onInterrupt}
        onImplementPlanInNewThread={props.onImplementPlanInNewThread}
      />
    </>
  );
});
